import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  ChevronDown, 
  QrCode, 
  Lock, 
  Shield, 
  Key, 
  BookOpen, 
  User, 
  Settings, 
  PlusCircle, 
  Copy, 
  Check, 
  Trash, 
  Share, 
  X, 
  Link2, 
  Download,
  ExternalLink,
  Tag,
  Search,
  Filter,
  Grid,
  Layout,
  Palette,
  Move,
  AlertCircle,
  Info,
  Save,
  MoreHorizontal,
  Edit,
  Sliders,
  BarChart2,
  Eye
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { STORAGE_KEYS } from '../utils/constants';
import { t } from '../i18n';

/**
 * Enhanced Pull-Down menu component
 * Memory Palace (Digital Vault) integration, 2FA Management, and QR Code content
 * Improved Custom QR section with better UX and animations
 * 
 * @param {Object} props - Component properties
 * @param {Function} props.onItemSelect - Function to call when menu item is selected
 * @param {Function} props.onShowSecurityVerification - Function for security verification display
 * @param {number} props.pullDownProgress - Progress of pull down (0-100)
 * @param {boolean} props.showPullDownMenu - Whether pull down menu is shown
 * @param {Function} props.closePullDownMenu - Function to close pull down menu
 * @param {boolean} props.isDarkMode - Whether dark mode is enabled
 * @param {Object} props.themeProps - Theme properties
 * @param {number} props.headerHeight - Height of the header
 * @param {number} props.bottomNavHeight - Height of the bottom navigation
 */
const PullDownMenu = ({ 
  onItemSelect, 
  onShowSecurityVerification, 
  pullDownProgress = 0,
  showPullDownMenu = false,
  closePullDownMenu,
  isDarkMode = false,
  themeProps = {},
  headerHeight = 0,
  bottomNavHeight = 0
}) => {
  // Use context values as fallback if props not provided directly
  const uiContext = useUI();
  const { user, is2FAEnabled } = useAuth();
  
  // Use props if provided, otherwise fall back to context values
  const actualPullDownProgress = pullDownProgress ?? uiContext?.pullDownProgress ?? 0;
  const actualShowPullDownMenu = showPullDownMenu ?? uiContext?.showPullDownMenu ?? false;
  const actualClosePullDownMenu = closePullDownMenu ?? uiContext?.closePullDownMenu;
  
  // State variables
  const [activeTab, setActiveTab] = useState('main');
  const [customLinks, setCustomLinks] = useState([]);
  const [newCustomLink, setNewCustomLink] = useState({ 
    name: '', 
    url: '', 
    category: 'general' 
  });
  const [showAddLinkForm, setShowAddLinkForm] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [showProfileQR, setShowProfileQR] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [urlError, setUrlError] = useState('');
  const [animateQR, setAnimateQR] = useState(false);
  const [showLinkStats, setShowLinkStats] = useState(false);
  const [selectedLinkStats, setSelectedLinkStats] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest', 'most-visits'
  
  // Define categories for QR codes
  const categories = [
    { id: 'all', name: t('common.all', 'Tümü') },
    { id: 'general', name: t('common.general', 'Genel') },
    { id: 'social', name: t('common.social', 'Sosyal Medya') },
    { id: 'work', name: t('common.work', 'İş') },
    { id: 'personal', name: t('common.personal', 'Kişisel') },
    { id: 'statistics', name: t('common.statistics', 'İstatistikler'), icon: BarChart2 }
  ];

  // QR İstatistikleri - Gerçek verilerden hesaplanacak
  const qrStatistics = useMemo(() => {
    // Default olarak boş bir istatistik objesi
    const stats = {
      totalQRs: 0,
      totalViews: 0,
      weeklyGrowth: 0,
      categoryData: [],
      topQRs: []
    };
    
    if (!customLinks || customLinks.length === 0) {
      return stats;
    }
    
    // Toplam QR kodu sayısı
    stats.totalQRs = customLinks.length;
    
    // Toplam görüntülenme sayısı
    stats.totalViews = customLinks.reduce((total, link) => total + (link.totalVisits || 0), 0);
    
    // Haftalık artış hesaplaması
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    let lastWeekViews = 0;
    let currentWeekViews = 0;
    
    customLinks.forEach(link => {
      if (link.visits && Array.isArray(link.visits)) {
        link.visits.forEach(visit => {
          const visitDate = new Date(visit.timestamp);
          if (visitDate >= oneWeekAgo) {
            currentWeekViews++;
          } else {
            const twoWeeksAgo = new Date(oneWeekAgo);
            twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 7);
            if (visitDate >= twoWeeksAgo && visitDate < oneWeekAgo) {
              lastWeekViews++;
            }
          }
        });
      }
    });
    
    // Haftalık büyüme oranı
    if (lastWeekViews > 0) {
      stats.weeklyGrowth = Math.round(((currentWeekViews - lastWeekViews) / lastWeekViews) * 100);
    } else if (currentWeekViews > 0) {
      stats.weeklyGrowth = 100; // Önceki hafta hiç görüntülenme yoksa ve bu hafta varsa %100 artış
    } else {
      stats.weeklyGrowth = 0;
    }
    
    // Kategori dağılımı hesaplaması
    const categoryCounts = {};
    let totalCategorizedQRs = 0;
    
    customLinks.forEach(link => {
      const category = link.category || 'general';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      totalCategorizedQRs++;
    });
    
    const categoryColors = {
      general: isDarkMode ? '#4f46e5' : '#4f46e5',  // Mavi-Mor
      social: isDarkMode ? '#06b6d4' : '#06b6d4',   // Turkuaz
      work: isDarkMode ? '#10b981' : '#10b981',     // Yeşil
      personal: isDarkMode ? '#ef4444' : '#ef4444', // Kırmızı
    };
    
    stats.categoryData = Object.keys(categoryCounts).map(category => {
      const categoryName = categories.find(c => c.id === category)?.name || t('common.general', 'Genel');
      return {
        name: categoryName,
        value: categoryCounts[category],
        color: categoryColors[category] || '#4f46e5'
      };
    });
    
    // "En çok görüntülenen QR kodları" için sıralanmış liste
    stats.topQRs = [...customLinks]
      .sort((a, b) => (b.totalVisits || 0) - (a.totalVisits || 0))
      .slice(0, 3)
      .map(link => ({
        name: link.name,
        views: link.totalVisits || 0,
        id: link.id
      }));
    
    return stats;
  }, [customLinks, isDarkMode, categories]);
  
  const menuRef = useRef(null);
  const contentRef = useRef(null);
  const qrCodeRef = useRef(null);

  // Get theme classes with defaults - Improved contrast for dark mode
  const {
    bgClass = isDarkMode ? 'bg-slate-900' : 'bg-white',
    textClass = isDarkMode ? 'text-gray-100' : 'text-gray-800',
    borderClass = isDarkMode ? 'border-slate-700' : 'border-gray-200',
    dividerClass = isDarkMode ? 'border-slate-700' : 'border-gray-200',
    hoverClass = isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50',
    activeClass = isDarkMode ? 'bg-slate-800' : 'bg-gray-50',
    shadowClass = isDarkMode ? 'shadow-dark' : 'shadow-md',
    iconClass = isDarkMode ? 'text-blue-400' : 'text-blue-600',
  } = themeProps;

  // Load custom links from localStorage
  useEffect(() => {
    const savedLinks = getFromStorage(STORAGE_KEYS.CUSTOM_LINKS, []);
    if (savedLinks) {
      setCustomLinks(savedLinks);
    }
  }, []);

  // Calculate menu height dynamically based on content
  useEffect(() => {
    if (contentRef.current && actualShowPullDownMenu) {
      const height = Math.min(contentRef.current.scrollHeight + 16, window.innerHeight * 0.85);
      setMenuHeight(height);
    } else {
      setMenuHeight(0);
    }
  }, [actualShowPullDownMenu, activeTab, customLinks, showAddLinkForm, selectedCategory, searchQuery]);

  // Set initial tab when opening menu
  useEffect(() => {
    if (actualShowPullDownMenu) {
      setActiveTab('main');
    }
  }, [actualShowPullDownMenu]);

  // Reset search query when changing tab
  useEffect(() => {
    setSearchQuery('');
    setSelectedCategory('all');
  }, [activeTab]);

  // Add QR code animation effect
  useEffect(() => {
    if (showQRCode || showProfileQR) {
      // Short delay before starting the animation
      setTimeout(() => {
        setAnimateQR(true);
      }, 300);
    } else {
      setAnimateQR(false);
    }
  }, [showQRCode, showProfileQR]);

  // Handle showing link statistics
  const handleShowLinkStats = (link) => {
    setSelectedLinkStats(link);
    setShowLinkStats(true);
  };

  // Handle recording link view/scan
  const recordLinkVisit = (linkId, metadata = {}) => {
    const updatedLinks = customLinks.map(link => {
      if (link.id === linkId) {
        const newVisit = {
          timestamp: new Date().toISOString(),
          ...metadata
        };
        
        return {
          ...link,
          visits: [...(link.visits || []), newVisit],
          totalVisits: (link.totalVisits || 0) + 1,
          lastVisited: new Date().toISOString()
        };
      }
      return link;
    });
    
    setCustomLinks(updatedLinks);
    saveToStorage(STORAGE_KEYS.CUSTOM_LINKS, updatedLinks);
    return true;
  };

  // Calculate menu style with improved positioning and animations
  const menuStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    height: menuHeight > 0 ? `${menuHeight}px` : 'auto',
    maxHeight: '90vh',
    transform: actualShowPullDownMenu 
      ? 'translateY(0)' 
      : `translateY(-${Math.min(100 - actualPullDownProgress, 100)}%)`,
    transition: 'transform 0.3s cubic-bezier(0.17, 0.93, 0.38, 1), height 0.3s cubic-bezier(0.17, 0.93, 0.38, 1)',
    overflowY: 'auto',
    zIndex: 999,
    backdropFilter: 'blur(12px)', 
    WebkitBackdropFilter: 'blur(12px)',
    visibility: actualPullDownProgress > 0 || actualShowPullDownMenu ? 'visible' : 'hidden',
    display: 'block'
  };

  // Calculate overlay style with smoother transitions
  const overlayStyle = {
    opacity: Math.min(actualPullDownProgress / 100, 0.7),
    visibility: actualPullDownProgress > 0 || actualShowPullDownMenu ? 'visible' : 'hidden',
    transition: 'opacity 0.3s ease-out, visibility 0.3s ease-out',
    pointerEvents: actualShowPullDownMenu ? 'auto' : 'none',
    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)'
  };

  // Filter links based on search query and category
  const filteredLinks = customLinks.filter(link => {
    const matchesSearch = searchQuery 
      ? (link.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
         link.url.toLowerCase().includes(searchQuery.toLowerCase()))
      : true;
    
    const matchesCategory = selectedCategory === 'all' || link.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Validate URL format
  const validateUrl = (url) => {
    try {
      // Simple validation - try to create a URL object
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      new URL(url);
      setUrlError('');
      return url;
    } catch (e) {
      setUrlError(t('common.invalidUrl', 'Geçerli bir URL giriniz'));
      return false;
    }
  };
  
  // Download QR code as image
  const downloadQRCode = () => {
    if (qrCodeRef.current) {
      const imgSrc = qrCodeRef.current.src;
      const link = document.createElement('a');
      link.href = imgSrc;
      link.download = `${qrData.name || 'qrcode'}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle vault click
  const handleVaultClick = () => {
    if (actualClosePullDownMenu) actualClosePullDownMenu();
    
    if (onShowSecurityVerification) {
      onShowSecurityVerification(() => {
        if (onItemSelect) onItemSelect('digitalVault');
      });
    } else if (onItemSelect) {
      onItemSelect('digitalVault');
    }
  };

  // Handle 2FA click
  const handle2FAClick = () => {
    if (actualClosePullDownMenu) actualClosePullDownMenu();
    if (onItemSelect) onItemSelect('twoFactorAuth');
  };

  // Handle custom QR click
  const handleCustomQRClick = () => {
    setActiveTab('customQR');
  };

  // Handle profile QR click
  const handleProfileQRClick = () => {
    if (actualClosePullDownMenu) actualClosePullDownMenu();
    showProfileQRCode();
  };

  // Show profile QR code
  const showProfileQRCode = () => {
    // Create QR content from user profile info
    const profileData = {
      username: user?.username || 'anonymous',
      id: user?.id || 'unknown',
      displayName: user?.displayName || 'User',
      profileShareUrl: `https://dapps.app/profile/${user?.username || 'anonymous'}`
    };

    // Set QR content
    setQrData({
      type: 'profile',
      data: JSON.stringify(profileData),
      name: t('profile.qrId', 'Profil QR Kodum')
    });
    
    setShowProfileQR(true);
    
    // Close the pull down menu when showing profile QR
    if (actualClosePullDownMenu) actualClosePullDownMenu();
  };

  // Show QR code
  const handleShowQRCode = (link) => {
    setQrData({
      type: 'link',
      data: link.url,
      name: link.name,
      category: link.category,
      linkId: link.id, // Store the linkId for tracking purposes
      trackingId: link.trackingId
    });
    setShowQRCode(true);
    
    // Record this QR code display as a view
    if (link.id) {
      recordLinkVisit(link.id, { action: 'qr_displayed' });
    }
    
    // Close the pull down menu when showing QR code
    if (actualClosePullDownMenu) actualClosePullDownMenu();
  };

  // Add new custom link
  const handleAddCustomLink = () => {
    if (newCustomLink.name && newCustomLink.url) {
      // Validate URL
      const validatedUrl = validateUrl(newCustomLink.url);
      if (!validatedUrl) return;
      
      // Generate a unique tracking ID for this link
      const trackingId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      const updatedLinks = [...customLinks, {
        id: Date.now(),
        name: newCustomLink.name,
        url: validatedUrl,
        category: newCustomLink.category || 'general',
        createdAt: new Date().toISOString(),
        trackingId: trackingId,
        visits: [],  // Array to store visit timestamps and metadata
        totalVisits: 0,
        lastVisited: null
      }];
      
      setCustomLinks(updatedLinks);
      saveToStorage(STORAGE_KEYS.CUSTOM_LINKS, updatedLinks);
      setNewCustomLink({ name: '', url: '', category: 'general' });
      setShowAddLinkForm(false);
    }
  };

  // Delete custom link
  const handleDeleteCustomLink = (id) => {
    const updatedLinks = customLinks.filter(link => link.id !== id);
    setCustomLinks(updatedLinks);
    saveToStorage(STORAGE_KEYS.CUSTOM_LINKS, updatedLinks);
  };

  // Copy link
  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopiedLink(url);
        setTimeout(() => setCopiedLink(null), 2000);
      })
      .catch(err => console.error(t('common.copyFailed', 'Kopyalama başarısız:'), err));
  };

  // QR Code Modal for links
  const renderQRCodeModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-sm w-full p-6 animate-fadeIn`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-bold text-lg ${textClass}`}>{qrData?.name}</h3>
          <button 
            className={`p-1 rounded-full ${hoverClass}`}
            onClick={() => setShowQRCode(false)}
          >
            <X size={20} className={textClass} />
          </button>
        </div>
        
        <div className="flex flex-col items-center justify-center mb-4">
          <div 
            className={`${animateQR ? 'animate-qrPopIn' : ''} p-4 bg-white rounded-lg mb-3`}
          >
            {qrData?.data && (
              <img 
                ref={qrCodeRef}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData.data)}`}
                alt="QR Code"
                className="w-48 h-48"
              />
            )}
          </div>
          
          {qrData?.category && (
            <span className={`px-2 py-1 text-xs rounded-full ${
              isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-100 text-gray-600'
            }`}>
              {categories.find(c => c.id === qrData.category)?.name || t('common.general', 'Genel')}
            </span>
          )}
        </div>
        
        <div className="flex justify-between items-center">
          <button
            className={`flex items-center px-3 py-2 rounded-lg ${
              isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            } transition-colors`}
            onClick={() => {
              navigator.clipboard.writeText(qrData?.data);
              setCopiedLink(qrData?.data);
              setTimeout(() => setCopiedLink(null), 2000);
            }}
          >
            {copiedLink === qrData?.data ? (
              <><Check size={16} className="mr-1 text-green-500" /> {t('common.copied', 'Kopyalandı')}</>
            ) : (
              <><Copy size={16} className="mr-1" /> {t('common.copyLink', 'Linki Kopyala')}</>
            )}
          </button>
          
          <button
            className={`flex items-center px-3 py-2 rounded-lg ${
              isDarkMode 
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            } transition-colors`}
            onClick={downloadQRCode}
          >
            <Download size={16} className="mr-1" /> {t('common.download', 'İndir')}
          </button>
        </div>
      </div>
    </div>
  );

  // Profile QR Code Modal
  const renderProfileQRModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-sm w-full p-6 animate-fadeIn`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-bold text-lg ${textClass}`}>{qrData?.name || t('profile.qrId', 'Profil QR Kodum')}</h3>
          <button 
            className={`p-1 rounded-full ${hoverClass}`}
            onClick={() => setShowProfileQR(false)}
          >
            <X size={20} className={textClass} />
          </button>
        </div>
        
        <div className="flex flex-col items-center justify-center mb-4">
          <div className={`${animateQR ? 'animate-qrPopIn' : ''} p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'} rounded-lg mb-3`}>
            {qrData?.data && (
              <img 
                ref={qrCodeRef}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData.data)}`}
                alt="Profile QR Code"
                className="w-48 h-48"
              />
            )}
          </div>
          
          <div className={`text-center ${textClass}`}>
            <p className="text-sm font-medium">{user?.displayName || 'User'}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>@{user?.username || 'username'}</p>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <button
            className={`flex items-center px-3 py-2 rounded-lg ${
              isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            } transition-colors`}
            onClick={() => {
              const profileUrl = `https://idapps.app/profile/${user?.username || 'anonymous'}`;
              navigator.clipboard.writeText(profileUrl);
              setCopiedLink(profileUrl);
              setTimeout(() => setCopiedLink(null), 2000);
            }}
          >
            {copiedLink === `https://idapps.app/profile/${user?.username || 'anonymous'}` ? (
              <><Check size={16} className="mr-1 text-green-500" /> {t('common.copied', 'Kopyalandı')}</>
            ) : (
              <><Copy size={16} className="mr-1" /> {t('common.copyProfileLink', 'Profil Linkini Kopyala')}</>
            )}
          </button>
          
          <button
            className={`flex items-center px-3 py-2 rounded-lg ${
              isDarkMode 
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            } transition-colors`}
            onClick={downloadQRCode}
          >
            <Download size={16} className="mr-1" /> {t('common.download', 'İndir')}
          </button>
        </div>
      </div>
    </div>
  );

  // Link Stats Modal
  const renderLinkStatsModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-sm w-full p-6 animate-fadeIn`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-bold text-lg ${textClass}`}>
            {t('statistics.linkStats', 'Bağlantı İstatistikleri')}
          </h3>
          <button 
            className={`p-1 rounded-full ${hoverClass}`}
            onClick={() => setShowLinkStats(false)}
          >
            <X size={20} className={textClass} />
          </button>
        </div>
        
        <div className="mb-4">
          <h4 className={`font-medium ${textClass}`}>{selectedLinkStats?.name}</h4>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {selectedLinkStats?.url}
          </p>
          <div className="mt-2">
            <span className={`text-xs px-2 py-1 rounded-full ${
              isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-100 text-gray-600'
            }`}>
              {categories.find(c => c.id === selectedLinkStats?.category)?.name || t('common.general', 'Genel')}
            </span>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <div className="flex justify-between items-center mb-2">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('common.totalViews', 'Toplam Görüntülenme')}:
            </span>
            <span className={`font-bold ${textClass}`}>
              {selectedLinkStats?.totalVisits || 0}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('common.lastViewed', 'Son Görüntülenme')}:
            </span>
            <span className={`${textClass}`}>
              {selectedLinkStats?.lastVisited 
                ? new Date(selectedLinkStats.lastVisited).toLocaleDateString() 
                : t('common.never', 'Hiç')}
            </span>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            className={`flex items-center px-3 py-2 rounded-lg ${
              isDarkMode 
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            } transition-colors`}
            onClick={() => setShowLinkStats(false)}
          >
            {t('common.close', 'Kapat')}
          </button>
        </div>
      </div>
    </div>
  );

  // Render the main menu content
  const renderMainMenu = () => (
    <>
      {/* Security Features */}
      <div className="mb-4">
        <div className={`flex items-center mb-2 ${textClass}`}>
          <Shield size={18} className="mr-2" />
          <span className="font-medium">{t('common.securityFeatures', 'Güvenlik Özellikleri')}</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <button 
            onClick={handleVaultClick}
            className={`flex flex-col items-center justify-center p-4 rounded-xl ${
              isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
            } ${textClass} transition-colors`}
          >
            <Lock size={24} className={`mb-2 ${iconClass}`} />
            <span className="text-center text-sm">{t('vault.digitalVault', 'Dijital Kasa')}</span>
          </button>
          
          <button 
            onClick={handle2FAClick}
            className={`flex flex-col items-center justify-center p-4 rounded-xl ${
              isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
            } ${textClass} transition-colors`}
          >
            <Key size={24} className={`mb-2 ${iconClass}`} />
            <span className="text-center text-sm">
              {is2FAEnabled 
                ? t('auth.manage2FA', '2FA Yönetimi') 
                : t('auth.setup2FA', '2FA Kurulumu')}
            </span>
          </button>
        </div>
      </div>
      
      {/* QR Features */}
      <div className="mb-4">
        <div className={`flex items-center mb-2 ${textClass}`}>
          <QrCode size={18} className="mr-2" />
          <span className="font-medium">{t('common.qrFeatures', 'QR Özellikleri')}</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <button 
            onClick={handleCustomQRClick}
            className={`flex flex-col items-center justify-center p-4 rounded-xl ${
              isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
            } ${textClass} transition-colors`}
          >
            <Link2 size={24} className={`mb-2 ${iconClass}`} />
            <span className="text-center text-sm">{t('qr.customQR', 'Özel QR Kodları')}</span>
          </button>
          
          <button 
            onClick={handleProfileQRClick}
            className={`flex flex-col items-center justify-center p-4 rounded-xl ${
              isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
            } ${textClass} transition-colors`}
          >
            <User size={24} className={`mb-2 ${iconClass}`} />
            <span className="text-center text-sm">{t('profile.qrId', 'Profil QR Kodum')}</span>
          </button>
        </div>
      </div>
      
      {/* Settings and Profile */}
      <div className="mb-2">
        <div className={`flex items-center mb-2 ${textClass}`}>
          <Settings size={18} className="mr-2" />
          <span className="font-medium">{t('common.settingsAndProfile', 'Ayarlar ve Profil')}</span>
        </div>
        
        <div className="space-y-1">
          <button 
            onClick={() => { 
              if (actualClosePullDownMenu) actualClosePullDownMenu();
              if (onItemSelect) onItemSelect('digitalDiscipline');
            }}
            className={`w-full flex items-center p-3 rounded-lg ${
              isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'
            } ${textClass} transition-colors`}
          >
            <Shield size={18} className="mr-3" />
            <span>{t('common.digitalDiscipline', 'Digital Discipline')}</span>
          </button>
          
          <button 
            onClick={() => { 
              if (actualClosePullDownMenu) actualClosePullDownMenu();
              if (onItemSelect) onItemSelect('settings');
            }}
            className={`w-full flex items-center p-3 rounded-lg ${
              isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'
            } ${textClass} transition-colors`}
          >
            <Settings size={18} className="mr-3" />
            <span>{t('common.settings', 'Ayarlar')}</span>
          </button>
          
          <button 
            onClick={() => { 
              if (actualClosePullDownMenu) actualClosePullDownMenu();
              if (onItemSelect) onItemSelect('profile?tab=statistics');
            }}
            className={`w-full flex items-center p-3 rounded-lg ${
              isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'
            } ${textClass} transition-colors`}
          >
            <BarChart2 size={18} className="mr-3" />
            <span>{t('statistics.statistics', 'İstatistikler')}</span>
          </button>
        </div>
      </div>
    </>
  );
  
  // Render the custom QR menu content
  const renderCustomQRMenu = () => (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <button 
            className={`p-2 rounded-full ${hoverClass} mr-2`}
            onClick={() => setActiveTab('main')}
          >
            <ChevronDown size={20} className={`transform rotate-90 ${textClass}`} />
          </button>
          <h2 className={`text-lg font-semibold ${textClass}`}>
            {t('qr.customQR', 'Özel QR Kodları')}
          </h2>
        </div>
        
        <button 
          className={`p-2 rounded-full ${hoverClass}`}
          onClick={() => setActiveTab('qrStats')}
        >
          <BarChart2 size={20} className={textClass} />
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="mb-4">
        <div className={`flex items-center ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
        } border rounded-lg p-2 mb-3`}>
          <Search size={18} className={`mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <input 
            type="text"
            placeholder={t('common.search', 'Ara...')}
            className={`bg-transparent border-none outline-none flex-1 ${textClass}`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.filter(cat => cat.id !== 'statistics').map(category => (
            <button 
              key={category.id}
              className={`px-2 py-1 rounded-lg text-xs font-medium ${
                selectedCategory === category.id
                  ? isDarkMode 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-indigo-50 text-indigo-700'
                  : isDarkMode
                    ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
        
        <div className="flex justify-between items-center mb-3">
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {filteredLinks.length} {t('common.results', 'sonuç')}
          </div>
          
          <div className="flex items-center">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className={`text-xs border rounded p-1 ${
                isDarkMode 
                  ? 'bg-slate-700 border-slate-600 text-white' 
                  : 'bg-white border-gray-200 text-gray-800'
              }`}
            >
              <option value="newest">{t('common.newest', 'En yeni')}</option>
              <option value="oldest">{t('common.oldest', 'En eski')}</option>
              <option value="most-visits">{t('common.popular', 'En popüler')}</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Custom links list */}
      {filteredLinks.length > 0 ? (
        <div className="space-y-3 mb-4">
          {filteredLinks
            .sort((a, b) => {
              if (sortOrder === 'newest') {
                return new Date(b.createdAt) - new Date(a.createdAt);
              } else if (sortOrder === 'oldest') {
                return new Date(a.createdAt) - new Date(b.createdAt);
              } else if (sortOrder === 'most-visits') {
                return (b.totalVisits || 0) - (a.totalVisits || 0);
              }
              return 0;
            })
            .map(link => (
              <div 
                key={link.id}
                className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}
              >
                <div className="flex justify-between mb-2">
                  <div>
                    <h3 className={`font-medium ${textClass}`}>{link.name}</h3>
                    <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {link.url}
                    </p>
                  </div>
                  <div>
                    <button 
                      onClick={() => handleShowQRCode(link)}
                      className={`p-1.5 rounded-full ${hoverClass}`}
                    >
                      <QrCode size={16} className={textClass} />
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {categories.find(c => c.id === link.category)?.name || t('common.general', 'Genel')}
                    </span>
                    
                    {(link.totalVisits > 0) && (
                      <button 
                        onClick={() => handleShowLinkStats(link)} 
                        className={`ml-2 flex items-center text-xs ${
                          isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Eye size={12} className="mr-1" />
                        {link.totalVisits}
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center">
                    <button 
                      onClick={() => handleCopyLink(link.url)}
                      className={`p-1.5 rounded-full ${hoverClass} mr-1`}
                    >
                      {copiedLink === link.url ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                      )}
                    </button>
                    
                    <button 
                      onClick={() => handleDeleteCustomLink(link.id)}
                      className={`p-1.5 rounded-full ${hoverClass}`}
                    >
                      <Trash size={16} className={isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className={`p-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {searchQuery || selectedCategory !== 'all'
            ? t('common.noResults', 'Sonuç bulunamadı')
            : t('qr.noCustomQR', 'Henüz özel QR kodunuz yok')}
        </div>
      )}
      
      {/* Add new link button */}
      {showAddLinkForm ? (
        <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <h3 className={`font-medium mb-3 ${textClass}`}>{t('qr.addNewLink', 'Yeni Bağlantı Ekle')}</h3>
          
          <div className="space-y-3">
            <div>
              <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('common.name', 'İsim')}
              </label>
              <input
                type="text"
                value={newCustomLink.name}
                placeholder={t('common.enterName', 'İsim girin')}
                onChange={(e) => setNewCustomLink({...newCustomLink, name: e.target.value})}
                className={`w-full p-2 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-slate-700 border-slate-600 text-white' 
                    : 'bg-white border-gray-200 text-gray-800'
                }`}
              />
            </div>
            
            <div>
              <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('common.url', 'URL')}
              </label>
              <input
                type="text"
                value={newCustomLink.url}
                placeholder="https://example.com"
                onChange={(e) => setNewCustomLink({...newCustomLink, url: e.target.value})}
                className={`w-full p-2 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-slate-700 border-slate-600 text-white' 
                    : 'bg-white border-gray-200 text-gray-800'
                }`}
              />
              {urlError && (
                <p className="text-xs text-red-500 mt-1">{urlError}</p>
              )}
            </div>
            
            <div>
              <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('common.category', 'Kategori')}
              </label>
              <select
                value={newCustomLink.category}
                onChange={(e) => setNewCustomLink({...newCustomLink, category: e.target.value})}
                className={`w-full p-2 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-slate-700 border-slate-600 text-white' 
                    : 'bg-white border-gray-200 text-gray-800'
                }`}
              >
                {categories.filter(cat => cat.id !== 'all' && cat.id !== 'statistics').map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setShowAddLinkForm(false)}
                className={`px-3 py-2 rounded ${
                  isDarkMode 
                    ? 'bg-slate-700 hover:bg-slate-600 text-gray-200' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                {t('common.cancel', 'İptal')}
              </button>
              
              <button
                onClick={handleAddCustomLink}
                className={`px-3 py-2 rounded ${
                  isDarkMode 
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                } ${!newCustomLink.name || !newCustomLink.url ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!newCustomLink.name || !newCustomLink.url}
              >
                {t('common.add', 'Ekle')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddLinkForm(true)}
          className={`flex items-center justify-center w-full p-3 rounded-lg ${
            isDarkMode 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          <PlusCircle size={16} className="mr-2" />
          {t('qr.addNewQR', 'Yeni QR Kodu Ekle')}
        </button>
      )}
    </>
  );
  
  // Render the QR stats menu content
  const renderQRStatsMenu = () => (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <button 
            className={`p-2 rounded-full ${hoverClass} mr-2`}
            onClick={() => setActiveTab('customQR')}
          >
            <ChevronDown size={20} className={`transform rotate-90 ${textClass}`} />
          </button>
          <h2 className={`text-lg font-semibold ${textClass}`}>
            {t('statistics.qrStatistics', 'QR İstatistikleri')}
          </h2>
        </div>
      </div>
      
      {/* Statistics Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            {t('common.totalQRs', 'Toplam QR')}
          </div>
          <div className={`text-xl font-bold ${textClass}`}>
            {qrStatistics.totalQRs}
          </div>
        </div>
        
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            {t('common.totalViews', 'Görüntülenme')}
          </div>
          <div className={`text-xl font-bold ${textClass}`}>
            {qrStatistics.totalViews}
          </div>
        </div>
        
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            {t('common.weeklyGrowth', 'Haftalık')}
          </div>
          <div className={`text-xl font-bold ${
            qrStatistics.weeklyGrowth > 0 
              ? 'text-green-500' 
              : qrStatistics.weeklyGrowth < 0 
                ? 'text-red-500' 
                : textClass
          }`}>
            {qrStatistics.weeklyGrowth > 0 ? '+' : ''}{qrStatistics.weeklyGrowth}%
          </div>
        </div>
      </div>
      
      {/* Category Distribution Chart */}
      {qrStatistics.categoryData.length > 0 && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'} mb-4`}>
          <h3 className={`font-medium text-base ${textClass} mb-3`}>
            {t('common.categoryDistribution', 'Kategori Dağılımı')}
          </h3>
          
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={qrStatistics.categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {qrStatistics.categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Top QR Codes */}
      {qrStatistics.topQRs.length > 0 && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <h3 className={`font-medium text-base ${textClass} mb-3`}>
            {t('qr.mostViewed', 'En Çok Görüntülenen QR Kodları')}
          </h3>
          
          <div className="space-y-3">
            {qrStatistics.topQRs.map((qr, index) => {
              const fullQrInfo = customLinks.find(link => link.id === qr.id);
              return (
                <div 
                  key={qr.id}
                  className={`p-3 rounded ${
                    isDarkMode 
                      ? index === 0 ? 'bg-amber-900/30' : 'bg-slate-700'
                      : index === 0 ? 'bg-amber-50' : 'bg-gray-100'
                  } flex justify-between items-center`}
                >
                  <div className="flex items-center">
                    <div className={`w-7 h-7 ${
                      isDarkMode 
                        ? index === 0 ? 'bg-amber-800' : 'bg-slate-600'
                        : index === 0 ? 'bg-amber-200' : 'bg-gray-200'
                    } rounded-full flex items-center justify-center mr-3 text-xs font-bold ${textClass}`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${textClass}`}>{qr.name}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {qr.views} {t('common.views', 'görüntülenme')}
                      </p>
                    </div>
                  </div>
                  
                  {fullQrInfo && (
                    <button 
                      onClick={() => handleShowQRCode(fullQrInfo)}
                      className={`p-1.5 rounded-full ${hoverClass}`}
                    >
                      <QrCode size={16} className={textClass} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="mt-4">
        <button
          onClick={() => { 
            if (actualClosePullDownMenu) actualClosePullDownMenu();
            if (onItemSelect) onItemSelect('qrStatistics');
          }}
          className={`flex items-center justify-center w-full p-3 rounded-lg ${
            isDarkMode 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          <BarChart2 size={16} className="mr-2" />
          {t('statistics.viewDetailedStats', 'Detaylı İstatistikleri Görüntüle')}
        </button>
      </div>
    </>
  );

  // Animate fade-in for modals
  const fadeInAnimation = `
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    .animate-fadeIn {
      animation: fadeIn 0.2s ease-out;
    }
    
    @keyframes qrPopIn {
      0% { opacity: 0; transform: scale(0.8); }
      70% { opacity: 1; transform: scale(1.05); }
      100% { opacity: 1; transform: scale(1); }
    }
    .animate-qrPopIn {
      animation: qrPopIn 0.5s ease-out forwards;
    }
  `;

  return (
    <>
      {/* Animate fade-in styles */}
      <style>{fadeInAnimation}</style>
      
      {/* Darkening background */}
      <div 
        className={`fixed inset-0 pointer-events-auto z-40`}
        style={overlayStyle}
        onClick={actualClosePullDownMenu}
      />
      
      {/* Pull down menu */}
      <div 
        ref={menuRef}
        className={`fixed top-0 left-0 right-0 ${bgClass} rounded-b-2xl ${shadowClass} z-50 ${
          isDarkMode ? 'border-b border-slate-700' : 'border-b border-gray-200'
        }`}
        style={menuStyle}
      >
        <div ref={contentRef} className="flex flex-col p-4">
          {/* Main bar - Grip mark */}
          <div className="flex justify-center mb-3">
            <div className={`w-10 h-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'} rounded-full`}></div>
          </div>
          
          {/* Render according to tab content */}
          {activeTab === 'main' && renderMainMenu()}
          {activeTab === 'customQR' && renderCustomQRMenu()}
          {activeTab === 'qrStats' && renderQRStatsMenu()}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && qrData && renderQRCodeModal()}
      
      {/* Profile QR Code Modal */}
      {showProfileQR && qrData && renderProfileQRModal()}

      {/* Link Stats Modal */}
      {showLinkStats && selectedLinkStats && renderLinkStatsModal()}
    </>
  );
};

export default PullDownMenu;